import { useQuery } from '@tanstack/react-query';
import { CLIENT_STATES, type ClientListView, type ClientRowView, type ClientState } from '@lp/contracts';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Plus } from 'lucide-react';
import { useIsSuperAdmin } from '@/lib/auth';
import { errorText, get } from '@/lib/api';
import { age, dateTime, num } from '@/lib/format';
import { useT } from '@/lib/i18n';
import { Badge, Empty, ErrorBox, Input, Kpi, Loading, PageHeader, Select } from '@/components/ui';
import { PriceText } from '@/components/edit-dialogs';

export const STATE_LABEL: Record<ClientState, string> = { testing: 'Testing', live: 'Live', suspended: 'Suspended', closed: 'Closed' };
const STATE_TONE: Record<ClientState, 'blue' | 'green' | 'amber' | 'gray'> = { testing: 'blue', live: 'green', suspended: 'amber', closed: 'gray' };

export function StateBadge({ state }: { state: ClientState }) {
  const t = useT();
  return <Badge tone={STATE_TONE[state]}>{t(STATE_LABEL[state])}</Badge>;
}

export function ProblemsCell({ row }: { row: ClientRowView }) {
  const t = useT();
  if (!row.problems.length) return <Badge tone="green">{t('None')}</Badge>;
  const errors = row.problems.some((p) => p.severity === 'error');
  return (
    <span className="block">
      <Badge tone={errors ? 'red' : 'amber'}>
        {row.problems.length} {t(row.problems.length === 1 ? 'problem' : 'problems')}
      </Badge>
      <span className="mt-0.5 block max-w-[240px] truncate text-xs text-ink-600" title={row.problems.map((p) => p.message).join('\n')}>
        {row.problems[0]!.message}
      </span>
    </span>
  );
}

export const useClients = () => useQuery({ queryKey: ['clients'], queryFn: () => get<ClientListView>('/platform/clients'), refetchInterval: 60_000 });

/** one row per client (master prompt §19). Read-only: a client's page is where you look closer. */
export function ClientsPage() {
  const t = useT();
  const nav = useNavigate();
  const list = useClients();
  const superAdmin = useIsSuperAdmin();
  const [q, setQ] = useState('');
  const [state, setState] = useState<'' | ClientState>('');
  const [problemsOnly, setProblemsOnly] = useState(false);
  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (list.data?.items ?? []).filter((r) => (!state || r.state === state) && (!problemsOnly || r.problems.length > 0) && (!needle || r.code.toLowerCase().includes(needle) || r.nameEn.toLowerCase().includes(needle) || r.nameKm.includes(q.trim())));
  }, [list.data, q, state, problemsOnly]);
  const s = list.data?.summary;

  return (
    <>
      <PageHeader
        title={t('Clients')}
        subtitle={t('Every client on one screen. Open a client to look closer.')}
        actions={
          superAdmin && (
            <Link to="/clients/new" className="inline-flex h-9 items-center gap-1.5 rounded-md bg-brand-600 px-3.5 text-sm font-medium text-white hover:bg-brand-700">
              <Plus size={14} /> {t('New client')}
            </Link>
          )
        }
      />
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Kpi label={t('Clients')} value={num(s?.total)} />
        <Kpi label={t('Live')} value={num(s?.live)} tone="good" />
        <Kpi label={t('Testing')} value={num(s?.testing)} />
        <Kpi label={t('Suspended')} value={num(s?.suspended)} />
        <Kpi label={t('Closed')} value={num(s?.closed)} />
        <Kpi label={t('With problems')} value={num(s?.withProblems)} tone={s?.withProblems ? 'bad' : 'default'} />
      </div>

      <div className="rounded-lg border border-ink-200 bg-white">
        <div className="flex flex-wrap items-center gap-2 border-b border-ink-100 p-3">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('Code or name')} className="h-8 w-56 text-xs" />
          <Select value={state} onChange={(e) => setState(e.target.value as '' | ClientState)} className="h-8 text-xs">
            <option value="">{t('All states')}</option>
            {CLIENT_STATES.map((k) => (
              <option key={k} value={k}>
                {t(STATE_LABEL[k])}
              </option>
            ))}
          </Select>
          <label className="flex items-center gap-1.5 text-xs text-ink-700">
            <input type="checkbox" checked={problemsOnly} onChange={(e) => setProblemsOnly(e.target.checked)} />
            {t('Problems only')}
          </label>
        </div>
        {list.isLoading ? (
          <div className="p-4">
            <Loading />
          </div>
        ) : list.error ? (
          <div className="p-4">
            <ErrorBox text={errorText(list.error)} />
          </div>
        ) : rows.length === 0 ? (
          <Empty title={list.data?.items.length ? t('No client matches') : t('No client yet')} sub={list.data?.items.length ? undefined : t('Create one with New client.')} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ink-100 text-left text-xs font-medium text-ink-500">
                  <th className="px-4 py-2">{t('Company')}</th>
                  <th className="px-4 py-2">{t('Package')}</th>
                  <th className="px-4 py-2">{t('Price')}</th>
                  <th className="px-4 py-2">{t('State')}</th>
                  <th className="px-4 py-2 text-right">{t('Users')}</th>
                  <th className="px-4 py-2 text-right">{t('Agents')}</th>
                  <th className="px-4 py-2">{t('Last activity')}</th>
                  <th className="px-4 py-2">{t('Problems')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} onClick={() => nav(`/clients/${r.id}`)} className="cursor-pointer border-b border-ink-100 last:border-0 hover:bg-ink-50" data-testid={`client-row-${r.code}`}>
                    <td className="px-4 py-2.5">
                      <span className="font-medium text-ink-900">{r.nameEn}</span> <span className="font-mono text-[11px] text-ink-500">{r.code}</span>
                      <span className="block text-xs text-ink-500">{r.nameKm}</span>
                    </td>
                    <td className="px-4 py-2.5 text-xs">{r.package ? r.package.name : <span className="text-red-600">{t('No package')}</span>}</td>
                    <td className="px-4 py-2.5 text-xs">
                      <PriceText price={r.price} />
                      {r.price?.source === 'client' && <span className="block text-[10px] text-brand-700">{t('Own price')}</span>}
                    </td>
                    <td className="px-4 py-2.5">
                      <StateBadge state={r.state} />
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{num(r.users)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{num(r.agents)}</td>
                    <td className="px-4 py-2.5 text-xs">
                      <span className="font-medium text-ink-800">{age(r.lastActivityAt)}</span>
                      <span className="block text-ink-500">{dateTime(r.lastActivityAt)}</span>
                    </td>
                    <td className="px-4 py-2.5">
                      <ProblemsCell row={r} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
